import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { arrayUnion, doc, getDoc, updateDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { FaUserPlus, FaUserCheck } from 'react-icons/fa';
import defaultAvatar from '../assets/svg/images/default-avatar.jpg';
import { useStatusContext } from '../utils/StatusContext';
import { useFollow } from '../utils/FollowContext';
import { useEffect, useState } from 'react';

export default function StatusItem({ status }) {
  const auth = getAuth();
  const navigate = useNavigate();
  const { viewedStatuses, markStatusAsViewed, currentUser } = useStatusContext();
  const {
    isFollowing,
    shouldFollowBack,
    toggleFollow,
    followers,
    isInitialized
  } = useFollow();
  const [userData, setUserData] = useState({
    profileImage: defaultAvatar,
    username: 'Unknown',
    followersCount: 0
  });
  const [isFollowLoading, setIsFollowLoading] = useState(false);
  const [isViewLoading, setIsViewLoading] = useState(false);
  const hasViewed = viewedStatuses.has(status.id);

  // Fetch status owner's basic data
  useEffect(() => {
    async function fetchUserData() {
      try {
        const userRef = doc(db, 'users', status.userRef);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const data = userSnap.data();
          setUserData({
            profileImage: data.photoURL || defaultAvatar,
            username: data.username || 'Unknown',
            followersCount: data.followers?.length || 0
          });
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    }
    fetchUserData();
  }, [status.userRef, followers]);

  // Helper function to create a notification
  const createNotification = async (type, recipientId, message, targetId = null) => {
    if (!currentUser || currentUser.uid === recipientId) return; // Don't notify self

    try {
      const notifRef = collection(db, `notifications/${recipientId}/userNotifications`);
      await addDoc(notifRef, {
        type,
        senderId: currentUser.uid,
        targetId,
        message,
        timestamp: serverTimestamp(),
        isRead: false
      });
    } catch (error) {
      console.error('Error creating notification:', error);
    }
  };

  // Handle viewing the status
  const handleViewStatus = async () => {
    if (!currentUser) {
      navigate('/sign-in');
      return;
    }

    setIsViewLoading(true);
    try {
      const statusRef = doc(db, 'status', status.id);
      await updateDoc(statusRef, {
        viewedBy: arrayUnion(currentUser.uid)
      });
      await markStatusAsViewed(status.id);
      navigate(`/status/${status.id}/full`);
    } catch (error) {
      console.error('Error viewing status:', error);
    } finally {
      setIsViewLoading(false);
    }
  };

  // Handle follow/unfollow with optimized UI feedback
  const handleFollow = async (e) => {
    e.stopPropagation();
    if (!currentUser) {
      navigate('/sign-in');
      return;
    }

    setIsFollowLoading(true);
    try {
      const wasFollowing = isFollowing(status.userRef);
      await toggleFollow(status.userRef);
      if (!wasFollowing) {
        // Notify only on follow, not unfollow
        const message = `started following you.`;
        await createNotification('follow', status.userRef, message);
      }
    } catch (error) {
      console.error('Error handling follow:', error);
    } finally {
      setIsFollowLoading(false);
    }
  };

  // Determine follow button state
  const getFollowState = () => {
    if (!isInitialized) {
      return {
        state: 'loading',
        className: 'bg-gray-500 text-white',
        icon: <FaUserPlus size={12} />,
        ariaLabel: 'Loading follow state'
      };
    }
    
    if (isFollowing(status.userRef)) {
      return {
        state: 'following',
        className: 'bg-black text-white hover:bg-gray-800',
        icon: <FaUserCheck size={12} />,
        ariaLabel: `Unfollow ${userData.username}`
      };
    }
    if (shouldFollowBack(status.userRef)) {
      return {
        state: 'follow-back',
        className: 'bg-blue-500 text-white hover:bg-blue-600',
        icon: <FaUserPlus size={12} />,
        ariaLabel: `Follow back ${userData.username}`
      };
    }
    return {
      state: 'follow',
      className: 'bg-[#FF1493] text-white hover:bg-[#FF8C00]',
      icon: <FaUserPlus size={12} />,
      ariaLabel: `Follow ${userData.username}`
    };
  };

  const followState = getFollowState();

  if (!isInitialized) {
    return (
      <div className="flex flex-col items-center mx-2">
        <div className="w-[60px] h-[60px] rounded-full bg-gray-700 animate-pulse"></div>
        <div className="w-[70px] h-4 mt-1 bg-gray-700 rounded animate-pulse"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center mx-2">
      {/* Status Circle */}
      <div className="relative">
        <button
          onClick={handleViewStatus}
          disabled={isViewLoading}
          className={`w-[60px] h-[60px] rounded-full p-[3px] transition-all duration-300 ${
            hasViewed
              ? 'bg-gray-500'
              : 'bg-gradient-to-r from-[#FF1493] via-[#A020F0] to-[#FF8C00]'
          } hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[#FF8C00] ${
            isViewLoading ? 'opacity-70' : ''
          }`}
          aria-label={`View status by ${userData.username}`}
        >
          <img
            src={userData.profileImage}
            alt={`${userData.username}'s profile`}
            className="w-full h-full rounded-full object-cover border-2 border-gray-900"
            onError={(e) => (e.target.src = defaultAvatar)}
          />
        </button>

        {/* Follow Button with dynamic states */}
        {currentUser?.uid !== status.userRef && (
          <button
            onClick={handleFollow}
            disabled={isFollowLoading || followState.state === 'loading'}
            className={`absolute bottom-0 right-0 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 shadow-md focus:outline-none focus:ring-2 focus:ring-[#FF8C00] ${
              isFollowLoading ? 'opacity-70' : ''
            } ${followState.className}`}
            aria-label={followState.ariaLabel}
          >
            {followState.icon}
          </button>
        )}
      </div>

      {/* Username with follower count */}
      <div className="text-center">
        <p className="text-white text-xs mt-1 truncate w-[70px]">
          {userData.username}
        </p>
      </div>
    </div>
  );
}